import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ShieldCheck, ShieldOff, ChevronLeft, ChevronRight, Download, ArrowUpDown } from 'lucide-react';
import { api } from '../../api/client';
import { downloadCsv } from '../../utils/csv';
import AdminUserDrawer from './AdminUserDrawer';

interface UserRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  emailVerified: boolean;
  createdAt: string;
  kycVerification: { verificationStatus: string; kycLevel: string } | null;
}

type SortKey = 'createdAt' | 'status';

export default function AdminUsers() {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const load = useCallback((q?: string, p = 1) => {
    setLoading(true);
    api
      .get('/admin/users', { params: { search: q || undefined, page: p, per_page: perPage } })
      .then((res) => {
        setUsers(res.data.items);
        setTotal(res.data.total);
        setPage(p);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(search, 1);
  }, [load, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(search, 1);
  };

  const toggleSuspend = async (u: UserRow) => {
    setUpdatingId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/status`, { isSuspended: !u.isSuspended });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isSuspended: !x.isSuspended } : x)));
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((prev) => !prev);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'createdAt') cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    else cmp = Number(a.isSuspended) - Number(b.isSuspended);
    return sortAsc ? cmp : -cmp;
  });

  const exportCsv = () => {
    downloadCsv(
      `bankably-users-page${page}.csv`,
      sortedUsers.map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phone: u.phoneNumber,
        kycStatus: u.kycVerification?.verificationStatus ?? 'PENDING',
        joined: u.createdAt,
        status: u.isSuspended ? 'Suspended' : 'Active',
      }))
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold text-textPrimary">Users ({total})</h1>
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-2.5 text-textTertiary" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="pl-9 pr-4 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm w-72 focus:outline-none focus:border-gold"
            />
          </form>
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-surface1 border border-border text-textSecondary hover:border-gold/30 hover:text-textPrimary transition"
          >
            <Download size={14} /> Export Page
          </button>
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">KYC</th>
              <th className="text-left py-3 px-4 cursor-pointer" onClick={() => toggleSort('createdAt')}>
                <span className="flex items-center gap-1">Joined <ArrowUpDown size={12} /></span>
              </th>
              <th className="text-left py-3 px-4 cursor-pointer" onClick={() => toggleSort('status')}>
                <span className="flex items-center gap-1">Status <ArrowUpDown size={12} /></span>
              </th>
              <th className="text-right py-3 px-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-textTertiary">Loading...</td>
              </tr>
            ) : sortedUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-textTertiary">No users found.</td>
              </tr>
            ) : (
              sortedUsers.map((u) => (
                <tr key={u.id} className="border-b border-border/60 hover:bg-surface2/50 transition">
                  <td className="py-3 px-4 text-textPrimary cursor-pointer" onClick={() => setSelectedUserId(u.id)}>
                    {u.firstName} {u.lastName}
                    {u.isAdmin && (
                      <span className="ml-2 text-xs bg-gold-gradient text-bg px-2 py-0.5 rounded-full font-semibold">ADMIN</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-textSecondary">{u.email}</td>
                  <td className="py-3 px-4 text-textTertiary">{u.kycVerification?.verificationStatus ?? 'PENDING'}</td>
                  <td className="py-3 px-4 text-textTertiary">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${u.isSuspended ? 'bg-errorSurface text-error' : 'bg-successSurface text-success'}`}>
                      {u.isSuspended ? 'Suspended' : 'Active'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {!u.isAdmin && (
                      <button
                        onClick={() => toggleSuspend(u)}
                        disabled={updatingId === u.id}
                        className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition disabled:opacity-50 ${
                          u.isSuspended
                            ? 'bg-success/15 hover:bg-success/25 text-success'
                            : 'bg-error/15 hover:bg-error/25 text-error'
                        }`}
                      >
                        {u.isSuspended ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                        {u.isSuspended ? 'Reactivate' : 'Suspend'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-textSecondary">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => load(search, page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface1 border border-border disabled:opacity-40 hover:border-gold/30 transition"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => load(search, page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface1 border border-border disabled:opacity-40 hover:border-gold/30 transition"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      <AdminUserDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onDeleted={() => load(search, page)}
      />
    </div>
  );
}
